/**
 * Ripia Auth API Client
 *
 * HTTP リクエストユーティリティ
 * Clerk認証対応
 * @module auth/shared/apiClient
 */

import { loadEncryptedToken } from '../../../utils/crypto';
import logger from '../../../utils/logger';
import { safeJsonParse } from '../../../utils/jsonParseUtils';
import { API_CONFIG, TOKEN_KEYS } from '../constants';
import { AuthError } from '../types';

/**
 * APIリクエストユーティリティ
 * FIX Issue #1179: Clerk認証対応
 */
export class AuthApiClient {
  /**
   * Clerkトークンを取得（VITE_USE_REAL_API時はClerk優先）
   * フォールバックとしてLocalStorageトークンも確認
   */
  static async getAuthToken(): Promise<string | null> {
    // 1. Clerk SDKからトークン取得を試行
    try {
      const clerk = (
        window as unknown as {
          Clerk?: { session?: { getToken: () => Promise<string | null> } };
        }
      ).Clerk;
      if (clerk?.session) {
        const clerkToken = await clerk.session.getToken();
        if (clerkToken) {
          return clerkToken;
        }
      }
    } catch (error) {
      logger.warn(
        '[AuthApiClient] Clerk token retrieval failed, falling back to localStorage:',
        error
      );
    }

    // 2. フォールバック: LocalStorageトークン（レガシー互換）
    const localToken = loadEncryptedToken(TOKEN_KEYS.ACCESS_TOKEN);
    return localToken || null;
  }

  /**
   * タイムアウト付きfetch
   */
  static async fetchWithTimeout(
    url: string,
    options: RequestInit,
    timeout: number = API_CONFIG.TIMEOUT
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new AuthError('リクエストがタイムアウトしました', 'TIMEOUT');
      }
      throw error;
    }
  }

  /**
   * レスポンスハンドリング
   */
  static async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await safeJsonParse(
        response,
        'AuthApiClient.handleResponse',
        { message: 'Network error' }
      );

      switch (response.status) {
        case 401:
          throw new AuthError(
            errorData.message || '認証が必要です',
            'UNAUTHORIZED',
            401
          );
        case 400:
          throw new AuthError(
            errorData.message || '不正なリクエストです',
            'VALIDATION_ERROR',
            400
          );
        case 403:
          throw new AuthError('アクセスが拒否されました', 'UNAUTHORIZED', 403);
        case 404:
          throw new AuthError(
            errorData.message || 'リソースが見つかりません',
            'NOT_FOUND',
            404
          );
        case 409:
          throw new AuthError(
            errorData.message || '競合が発生しました',
            'CONFLICT',
            409
          );
        case 429:
          throw new AuthError(
            'リクエスト数が上限を超えました',
            'RATE_LIMIT_EXCEEDED',
            429
          );
        case 500:
          throw new AuthError(
            'サーバーエラーが発生しました',
            'SERVER_ERROR',
            500
          );
        case 503:
          throw new AuthError(
            'サービスが一時的に利用できません',
            'SERVICE_UNAVAILABLE',
            503
          );
        default:
          throw new AuthError(
            errorData.message || `HTTP ${response.status} エラー`,
            'NETWORK_ERROR',
            response.status
          );
      }
    }

    return response.json();
  }

  /**
   * POST リクエスト
   */
  static async post<T>(endpoint: string, data: object): Promise<T> {
    const url = `${API_CONFIG.BASE_URL}${endpoint}`;
    const token = await this.getAuthToken();

    const response = await this.fetchWithTimeout(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(data),
    });

    return this.handleResponse<T>(response);
  }

  /**
   * GET リクエスト
   */
  static async get<T>(endpoint: string): Promise<T> {
    const url = `${API_CONFIG.BASE_URL}${endpoint}`;
    const token = await this.getAuthToken();

    const response = await this.fetchWithTimeout(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    return this.handleResponse<T>(response);
  }

  /**
   * PUT リクエスト
   */
  static async put<T>(endpoint: string, data: object): Promise<T> {
    const url = `${API_CONFIG.BASE_URL}${endpoint}`;
    const token = await this.getAuthToken();

    const response = await this.fetchWithTimeout(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(data),
    });

    return this.handleResponse<T>(response);
  }

  /**
   * PATCH リクエスト
   */
  static async patch<T>(endpoint: string, data: object): Promise<T> {
    const url = `${API_CONFIG.BASE_URL}${endpoint}`;
    const token = await this.getAuthToken();

    const response = await this.fetchWithTimeout(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(data),
    });

    return this.handleResponse<T>(response);
  }

  /**
   * DELETE リクエスト
   */
  static async delete<T>(endpoint: string): Promise<T> {
    const url = `${API_CONFIG.BASE_URL}${endpoint}`;
    const token = await this.getAuthToken();

    const response = await this.fetchWithTimeout(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    return this.handleResponse<T>(response);
  }
}

export default AuthApiClient;
